import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import { Button, Card, Empty, Flex, List, Modal, Popconfirm, Result, Skeleton, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import {
  useDeleteUserMessageMutation,
  useGetUserMessagesQuery,
  useMarkUserMessageAsReadMutation,
} from '@/features/user-messages/user-messages.api.ts'
import { UserMessage } from '@/features/user-messages/user-messages.def.ts'
import { UserMessageRowsGrid } from '@/features/user-messages/UserMessageRowsGrid.tsx'
import { PageLayout } from '@/components/layouts/PageLayout.tsx'
import { handleApiError } from '@/utils/api.tsx'
import { cn } from '@/utils/style.ts'

export const NotificationsPage = () => {
  const { data: messages = [], isError, isFetching, isLoading, refetch } = useGetUserMessagesQuery()
  const [markUserMessageAsRead] = useMarkUserMessageAsReadMutation()
  const [deleteUserMessage] = useDeleteUserMessageMutation()
  const [selectedMessage, setSelectedMessage] = useState<UserMessage | null>(null)
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null)

  const sortedMessages = useMemo(
    () => [...messages]
      .sort((left, right) => dayjs(right.createdAtUtc).valueOf() - dayjs(left.createdAtUtc).valueOf()),
    [messages],
  )

  const selectedMessageRows = useMemo(() => selectedMessage?.rows ?? [], [selectedMessage])

  const handleMarkAsRead = async (messageId: string) => {
    try {
      await markUserMessageAsRead(messageId).unwrap()
    } catch (error) {
      setSelectedMessage(current => current?.id === messageId ? { ...current, isRead: false } : current)
      handleApiError(error, 'Failed to mark notification as read')
    }
  }

  const handleOpenMessage = (message: UserMessage) => {
    setSelectedMessage(message.isRead ? message : { ...message, isRead: true })

    if (!message.isRead) {
      void handleMarkAsRead(message.id)
    }
  }

  const handleDelete = async (messageId: string) => {
    setDeletingMessageId(messageId)

    try {
      await deleteUserMessage(messageId).unwrap()
    } catch (error) {
      handleApiError(error, 'Failed to delete notification')
    } finally {
      setDeletingMessageId(null)
    }
  }

  const content = isLoading
    ? <Skeleton active paragraph={{ rows: 5 }} />
    : isError
      ? (
          <Result
            status="error"
            title="Unable to load notifications"
            subTitle="Please try again."
            extra={<Button type="primary" onClick={() => void refetch()}>Retry</Button>}
          />
        )
      : (
          <List
            dataSource={sortedMessages}
            locale={{ emptyText: <Empty description="You have no notifications" /> }}
            renderItem={(message) => {
              const isDeleting = deletingMessageId === message.id

              return (
                <List.Item
                  className={cn('mb-2 rounded-md', {
                    'border-l-4 border-blue-500 bg-blue-50': !message.isRead,
                    'bg-white': message.isRead,
                  })}
                  style={{ padding: '10px 16px' }}
                >
                  <div
                    className="grid w-full items-center gap-3"
                    style={{ gridTemplateColumns: '1fr 5fr 1fr 1fr' }}
                  >
                    <div>
                      <Tag color={message.isRead ? undefined : 'blue'}>{message.isRead ? 'Read' : 'Unread'}</Tag>
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      className="min-w-0 cursor-pointer"
                      onClick={() => handleOpenMessage(message)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleOpenMessage(message)
                        }
                      }}
                    >
                      <Typography.Paragraph
                        className="mb-0 min-w-0 whitespace-pre-line"
                        ellipsis={{ rows: 2 }}
                        title={message.content}
                      >
                        {message.content}
                      </Typography.Paragraph>
                    </div>
                    <Typography.Text type="secondary" className="text-center text-xs whitespace-nowrap">
                      {dayjs(message.createdAtUtc).format('DD MMM YYYY, HH:mm')}
                    </Typography.Text>
                    <div className="flex justify-end">
                      <Popconfirm
                        title="Delete this notification?"
                        description="This action cannot be undone."
                        okText="Delete"
                        okButtonProps={{ danger: true, loading: isDeleting }}
                        cancelButtonProps={{ disabled: isDeleting }}
                        onConfirm={() => handleDelete(message.id)}
                      >
                        <Button
                          danger
                          type="text"
                          icon={<DeleteOutlined />}
                          loading={isDeleting}
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    </div>
                  </div>
                </List.Item>
              )
            }}
          />
        )

  return (
    <PageLayout>
      <Card
        title="Notifications"
        extra={(
          <Button
            icon={<ReloadOutlined />}
            loading={isFetching}
            onClick={() => void refetch()}
          >
            Refresh
          </Button>
        )}
      >
        {content}
      </Card>
      <Modal
        open={selectedMessage !== null}
        title="Notification"
        footer={null}
        // Row detail is a full grid, so it needs far more room than a text-only body.
        width={selectedMessageRows.length > 0 ? '90%' : 880}
        onCancel={() => setSelectedMessage(null)}
      >
        {selectedMessage ? (
          <Flex vertical gap="middle">
            <Typography.Text type="secondary">
              {dayjs(selectedMessage.createdAtUtc).format('DD MMM YYYY, HH:mm')}
            </Typography.Text>
            <Typography.Text className="break-words whitespace-pre-line">
              {selectedMessage.content}
            </Typography.Text>
            {selectedMessageRows.length > 0 ? <UserMessageRowsGrid rows={selectedMessageRows} /> : null}
          </Flex>
        ) : null}
      </Modal>
    </PageLayout>
  )
}
